import React, { useState } from 'react';
import { api } from '../services/api';
const formatNumber = (value) => {
  if (value === null || value === undefined) return '—';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return String(value);
  return parsed.toLocaleString();
};

const formatHistoryAccess = (value) => {
  if (value === null || value === undefined) return '—';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return String(value);
  if (parsed === 999) return 'All Historic Data';
  if (parsed === 0) return 'Latest Year Only';
  if (parsed === 1) return '1 Year Back';
  return `${parsed} Years Back`;
};

const formatDateValue = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', { timeZone: 'UTC' });
};

const formatAccessThroughDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  if (
    parsed.getUTCHours() === 0 &&
    parsed.getUTCMinutes() === 0 &&
    parsed.getUTCSeconds() === 0 &&
    parsed.getUTCMilliseconds() === 0
  ) {
    parsed.setUTCDate(parsed.getUTCDate() - 1);
  }
  return parsed.toLocaleDateString('en-US', { timeZone: 'UTC' });
};
const formatCadence = (value) => {
  if (!value) return '—';
  return value.charAt(0).toUpperCase() + value.slice(1);
};
const getActivePriceLabel = (subscription) => {
  if (!subscription?.billing_cadence) return 'Current Price';
  return subscription.billing_cadence === 'annual' ? 'Annual Price' : 'Monthly Price';
};

const getActivePriceValue = (subscription) => {
  if (!subscription) return '—';
  const cents = subscription.billing_cadence === 'annual'
    ? subscription.price_annual_cents
    : subscription.price_monthly_cents;
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString()}`;
};

const getActivePriceCents = (subscription) => {
  if (!subscription) return null;
  return subscription.billing_cadence === 'annual'
    ? subscription.price_annual_cents
    : subscription.price_monthly_cents;
};

const formatRateLimitValue = (value) => {
  if (value === null || value === undefined) return 'Unlimited';
  return formatNumber(value);
};

const formatMoney = (cents) => {
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString()}`;
};

const formatCurrencyCode = (value) => String(value || 'usd').toUpperCase();

const formatEntitlementChangeValue = (key, value) => {
  if (key === 'api_rate_limit') return `${formatRateLimitValue(value)} per month`;
  if (key === 'api_src_limit') return value || 'All Data Sources';
  if (key === 'api_src_derived_flag' || key === 'excel_plugin_flag') return value ? 'Enabled' : 'Disabled';
  if (key === 'api_hist_limit') return formatHistoryAccess(value);
  return value ?? '—';
};

const EntitlementChangesTable = ({ changes }) => {
  if (!Array.isArray(changes) || changes.length === 0) {
    return <div className="text-muted small">No entitlement changes.</div>;
  }
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th>Entitlement</th>
            <th>Current</th>
            <th>New</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((change) => (
            <tr key={change.key}>
              <td>{change.label}</td>
              <td>{formatEntitlementChangeValue(change.key, change.current)}</td>
              <td>{formatEntitlementChangeValue(change.key, change.target)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AccountFeatureChanges = ({ changes, effectiveLabel }) => {
  const visibleChanges = Array.isArray(changes)
    ? changes.filter((change) => change?.key === 'sso_custom_idp')
    : [];
  if (visibleChanges.length === 0) return null;
  return (
    <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
      <div className="fw-semibold mb-2">Account Features Update</div>
      {visibleChanges.map((change) => (
        <div key={change.key}>
          {change.target ? 'SSO / Custom IdP becomes available.' : 'SSO / Custom IdP will be disabled.'}
        </div>
      ))}
      {effectiveLabel && <div className="text-muted mt-2">{effectiveLabel}</div>}
    </div>
  );
};

const formatPlanVersionLabel = ({ name, code, versionCode }) => {
  const primary = name || code || '—';
  return versionCode ? `${primary} (${versionCode})` : primary;
};

const OpsOrgSummary = ({ summary, loading, error, onRefresh }) => {
  const statusBadgeClass = String(summary?.org_status || '').toLowerCase() === 'closed' ? 'bg-secondary' : 'bg-success';
  const statusLabel = String(summary?.org_status || '').toLowerCase() === 'closed' ? 'Closed' : 'Active';
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyStatus, setApplyStatus] = useState({ type: '', message: '' });

  const handleApplyInternalEntitlements = async () => {
    if (!summary?.org_id || applyLoading) return;
    setApplyLoading(true);
    setApplyStatus({ type: '', message: '' });
    try {
      const response = await api.post(`/acct-mgmt/v1/orgs/${summary.org_id}/actions/apply-internal-entitlements`);
      setApplyStatus({ type: 'success', message: response.data?.message || 'Internal entitlements applied.' });
      setApplyLoading(false);
      if (typeof onRefresh === 'function') {
        Promise.resolve(onRefresh()).catch(() => {});
      }
      return;
    } catch (err) {
      setApplyStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to apply internal entitlements.' });
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <div>
      {loading && <div className="text-muted">Loading organization summary...</div>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {!loading && summary && (
        <div className="card">
          <div className="card-body">
              <h5 className="card-title">Organization Summary</h5>
              {applyStatus.message && (
                <div className={`alert alert-${applyStatus.type}`} role="alert">
                  {applyStatus.message}
                </div>
              )}
              <div className="fw-semibold mb-2">Profile</div>
              <div className="row g-3">
              <div className="col-md-4">
                <div className="text-muted small">Organization Name</div>
                <div className="fw-semibold">{summary.org_name}</div>
              </div>
              <div className="col-md-4">
                <div className="text-muted small">Account Type</div>
                <div className="fw-semibold">
                  {summary.org_type === 'type-org' ? 'Organization' : summary.org_type === 'type-ind' ? 'Individual' : '—'}
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-muted small">Status</div>
                <div className="fw-semibold">
                  <span className={`badge ${statusBadgeClass}`}>{statusLabel}</span>
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-muted small">Registration Date</div>
                <div className="fw-semibold">{summary.created_at_formatted}</div>
              </div>              
              <div className="col-md-4">
                <div className="text-muted small">Organization Email</div>
                <div className="fw-semibold">{summary.org_email || '—'}</div>
              </div>
              <div className="col-md-4">
                <div className="text-muted small"># Users</div>
                <div className="fw-semibold">{summary.user_count}</div>
              </div>
              <div className="col-md-4">
                <div className="text-muted small">Internal</div>
                <div className="fw-semibold d-flex align-items-center gap-2 flex-wrap">
                  <span>{summary.is_internal ? 'Yes' : 'No'}</span>
                  {summary.is_internal ? (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleApplyInternalEntitlements}
                      disabled={applyLoading}
                    >
                      {applyLoading ? 'Applying...' : 'Apply Internal Entitlements'}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="col-12">
                <div className="text-muted small">Billing Address</div>
                <div className="fw-semibold">
                  {summary.billing_addr_street || summary.billing_addr_street_2 || summary.billing_addr_city || summary.billing_addr_country ? (
                    <>
                      {summary.billing_addr_street || '—'}
                      {summary.billing_addr_street_2 ? `, ${summary.billing_addr_street_2}` : ''}
                      {summary.billing_addr_city || summary.billing_addr_state || summary.billing_addr_zip || summary.billing_addr_country ? (
                        `, ${summary.billing_addr_city || ''}${summary.billing_addr_state ? `, ${summary.billing_addr_state}` : ''} ${summary.billing_addr_zip || ''}${summary.billing_addr_country ? `, ${summary.billing_addr_country}` : ''}`
                      ) : ''}
                    </>
                  ) : (
                    '—'
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-muted small">Billing Legal Name</div>
                <div className="fw-semibold">{summary.billing_legal_name || '—'}</div>
              </div>
              <div className="col-md-4">
                <div className="text-muted small">Tax Exempt Status</div>
                <div className="fw-semibold text-capitalize">{String(summary.tax_exempt_status || 'none').replace(/_/g, ' ')}</div>
              </div>
              <div className="col-md-4">
                <div className="text-muted small">Tax ID</div>
                <div className="fw-semibold">
                  {summary.tax_id_value ? `${summary.tax_id_type || 'ID'}: ${summary.tax_id_value}` : '—'}
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-top">
              <div className="fw-semibold mb-2">Authentication Settings</div>
              <div className="row g-3">
                <div className="col-12">
                  <div className="text-muted small mb-2">Allowed Sign-In Methods</div>
                  <div className="d-flex flex-wrap gap-1">
                    {(summary.allowed_methods || []).length === 0 ? (
                      <span className="text-muted">None</span>
                    ) : (
                      summary.allowed_methods.map((method) => {
                        const isIncomplete = method.status === 'incomplete';
                        const isOidc = method.type === 'oidc' || String(method.label || '').startsWith('OIDC:');
                        const badgeClass = isIncomplete
                          ? 'bg-warning text-dark'
                          : isOidc
                            ? 'bg-info text-dark'
                            : 'bg-success';
                        return (
                          <span
                            key={`${method.label}-${method.status || 'enabled'}`}
                            className={`badge ${badgeClass}`}
                          >
                            {isIncomplete ? `${method.label} (Incomplete)` : method.label}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">MFA Required</div>
                  <div className="fw-semibold">{summary.mfa_required ? 'Yes' : 'No'}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Channel Policy</div>
                  <div className="fw-semibold">
                    {(() => {
                      switch (summary.mfa_channel_policy) {
                        case 'totp_only':
                          return 'Authenticator App Only';
                        case 'totp_preferred':
                          return 'Authenticator App Preferred';
                        case 'sms_only':
                          return 'SMS Only';
                        case 'sms_preferred':
                          return 'SMS Preferred';
                        case 'email_only':
                          return 'Email Only';
                        case 'any':
                          return 'Any (User Preference)';
                        default:
                          return '—';
                      }
                    })()}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">OAuth MFA Required</div>
                  <div className="fw-semibold">{summary.mfa_require_oauth ? 'Yes' : 'No'}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">SSO / Custom IdP Entitlement</div>
                  <div className="fw-semibold">{summary.sso_custom_idp_entitlement?.allowed ? 'Available' : 'Not entitled'}</div>
                  <div className="text-muted small">
                    {summary.sso_custom_idp_entitlement?.source === 'internal_override'
                      ? 'Internal org override'
                      : summary.sso_custom_idp_entitlement?.source === 'trial_support_override'
                        ? 'Trial support override'
                      : summary.sso_custom_idp_entitlement?.source === 'plan_version'
                        ? 'Current plan version'
                        : 'Requires Enterprise'}
                  </div>
                </div>
                <div className="col-12">
                  <div className="text-muted small mb-2">OIDC Providers</div>
                  {(summary.oidc_providers || []).length === 0 ? (
                    <div className="text-muted small">None configured.</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-1">
                      {summary.oidc_providers.map((provider) => (
                        <span
                          key={provider.id}
                          className={`badge ${provider.usable ? 'bg-info text-dark' : 'bg-secondary'}`}
                          title={`PKCE ${provider.pkce_enabled === false ? 'disabled' : 'enabled'}; token auth ${provider.token_endpoint_auth_method || 'client_secret_post'}`}
                        >
                          {provider.name || 'Custom'} {provider.usable ? '(usable)' : provider.enabled ? '(blocked)' : '(disabled)'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-top">
              <div className="fw-semibold mb-2">Subscription</div>
              {summary.subscription?.is_internal ? (
                <div className="text-muted">Internal organization. Subscription billing does not apply.</div>
              ) : (
                <>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="text-muted small">Current Plan</div>
                      <div className="fw-semibold">{summary.subscription?.plan_name || 'No Active Subscription'}</div>
                      <div className="text-muted small mt-1">
                        Plan: {summary.subscription?.plan_code || '—'} / Version: {summary.subscription?.version_code || '—'} / Version ID: {summary.subscription?.plan_version_id || '—'}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small">Status</div>
                      <div className="fw-semibold text-capitalize">{String(summary.subscription?.status || 'none').replace(/_/g, ' ')}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small">Billing Cadence</div>
                      <div className="fw-semibold text-capitalize">{summary.subscription?.billing_cadence || '—'}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small">{getActivePriceLabel(summary.subscription)}</div>
                      <div className="fw-semibold">{getActivePriceValue(summary.subscription)}</div>
                      <div className="text-muted small mt-1">Plus tax, where applicable.</div>
                    </div>
                    {summary.subscription?.status === 'trialing' && (
                      <div className="col-md-4">
                        <div className="text-muted small">Trial Ends</div>
                        <div className="fw-semibold">{formatAccessThroughDate(summary.subscription?.trial_end)}</div>
                      </div>
                    )}
                    {['active', 'scheduled_downgrade', 'scheduled_cancel'].includes(summary.subscription?.raw_status || summary.subscription?.status) && (
                      <div className="col-md-4">
                        <div className="text-muted small">Next Renewal / Charge Date</div>
                        <div className="fw-semibold">{formatDateValue(summary.subscription?.billing_period_end)}</div>
                      </div>
                    )}
                    <div className="col-md-4">
                      <div className="text-muted small">Stripe Customer</div>
                      <div className="fw-semibold">{summary.stripe_customer_id || 'Not Synced'}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small">Stripe Subscription</div>
                      <div className="fw-semibold">{summary.subscription?.stripe_subscription_id || 'Not Synced'}</div>
                    </div>
                  </div>
                  {summary.subscription?.scheduled_cancellation && (
                    <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                      <div className="fw-semibold mb-1">Scheduled cancellation</div>
                      <div>
                        <span className="text-muted">Access through:</span>{' '}
                        <span className="fw-semibold">{formatAccessThroughDate(summary.subscription.scheduled_cancellation.access_through)}</span>
                      </div>
                      <div>
                        <span className="text-muted">Plan:</span>{' '}
                        <span className="fw-semibold">
                          {formatPlanVersionLabel({
                            name: summary.subscription.plan_name,
                            code: summary.subscription.plan_code,
                            versionCode: summary.subscription.version_code,
                          })}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted">Billing cadence:</span>{' '}
                        <span className="fw-semibold">{formatCadence(summary.subscription.billing_cadence)}</span>
                      </div>
                      <div>
                        <span className="text-muted">Recurring charge:</span>{' '}
                        <span className="fw-semibold">{formatMoney(getActivePriceCents(summary.subscription))}</span>
                      </div>
                      <div className="text-muted mt-2">
                        Automatic renewal is scheduled to stop at the end of the current term.
                      </div>
                    </div>
                  )}
                  {summary.subscription?.scheduled_change && (
                    <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                      <div className="fw-semibold mb-1">Scheduled Subscription Update</div>
                      <div>
                        <span className="text-muted">Change effective:</span>{' '}
                        <span className="fw-semibold">{formatDateValue(summary.subscription.scheduled_change.effective_on)}</span>
                      </div>
                      <div>
                        <span className="text-muted">Plan:</span>{' '}
                        <span className="fw-semibold">
                          {formatPlanVersionLabel({
                            name: summary.subscription.plan_name,
                            code: summary.subscription.plan_code,
                            versionCode: summary.subscription.version_code,
                          })} to {formatPlanVersionLabel({
                            name: summary.subscription.scheduled_change.plan_name,
                            code: summary.subscription.scheduled_change.plan_code,
                            versionCode: summary.subscription.scheduled_change.version_code,
                          })}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted">Cadence:</span>{' '}
                        <span className="fw-semibold">
                          {formatCadence(summary.subscription.billing_cadence)} to {formatCadence(summary.subscription.scheduled_change.billing_cadence)}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted">Recurring charge:</span>{' '}
                        <span className="fw-semibold">
                          {formatMoney(getActivePriceCents(summary.subscription))} to {formatMoney(summary.subscription.scheduled_change.recurring_price_cents)}
                        </span>
                      </div>
                      <div className="text-muted mt-2">
                        Pricing and access are current estimates.
                      </div>
                      <div className="text-muted">
                        Final terms resolve from the active default on the scheduled conversion date.
                      </div>
                    </div>
                  )}
                  {summary.subscription?.grandfather_sunset && (
                    <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                      <div className="fw-semibold mb-1">Legacy Pricing Update</div>
                      <div>
                        <span className="text-muted">First affected renewal:</span>{' '}
                        <span className="fw-semibold">{formatDateValue(summary.subscription.grandfather_sunset.effective_change_date || summary.subscription.grandfather_sunset.grandfather_end_date)}</span>
                      </div>
                      <div>
                        <span className="text-muted">Plan:</span>{' '}
                        <span className="fw-semibold">
                          {formatPlanVersionLabel({
                            name: summary.subscription.plan_name,
                            code: summary.subscription.plan_code,
                            versionCode: summary.subscription.grandfather_sunset.current_version_code || summary.subscription.version_code,
                          })} to {formatPlanVersionLabel({
                            name: summary.subscription.grandfather_sunset.target_plan_name,
                            code: summary.subscription.grandfather_sunset.target_plan_code,
                            versionCode: summary.subscription.grandfather_sunset.target_version_code,
                          })}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted">Recurring charge:</span>{' '}
                        <span className="fw-semibold">
                          {formatMoney(summary.subscription.grandfather_sunset.current_price_cents)} to {formatMoney(summary.subscription.grandfather_sunset.target_price_cents)}
                        </span>
                      </div>
                      <div className="text-muted mt-2">
                        Grandfather end: {formatDateValue(summary.subscription.grandfather_sunset.grandfather_end_date)}.
                      </div>
                      <div className="text-muted">
                        {summary.subscription.grandfather_sunset.price_changes ? 'Price changes at the affected renewal.' : 'Recurring price does not change.'}
                      </div>
                    </div>
                  )}
                  <div className="mt-4 pt-3 border-top">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="text-muted small">Prior Year Spend</div>
                        <div className="fw-semibold">{formatMoney(summary.subscription_spend?.prior_year_spend_cents)}</div>
                      </div>
                      <div className="col-md-4">
                        <div className="text-muted small">Current Year Spend</div>
                        <div className="fw-semibold">{formatMoney(summary.subscription_spend?.current_year_spend_cents)}</div>
                      </div>
                      <div className="col-md-4">
                        <div className="text-muted small">Lifetime Spend</div>
                        <div className="fw-semibold">{formatMoney(summary.subscription_spend?.lifetime_spend_cents)}</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="mt-4 pt-3 border-top">
              <div className="fw-semibold mb-2">Billing History</div>
              {Array.isArray(summary.billing_history) && summary.billing_history.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Invoice Date</th>
                        <th>Subtotal</th>
                        <th>Tax</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Paid</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.billing_history.map((invoice) => (
                        <tr key={invoice.stripe_invoice_id}>
                          <td>{formatDateValue(invoice.invoice_created_at)}</td>
                          <td>{formatMoney(invoice.subtotal_cents ?? invoice.amount_due_cents)} {formatCurrencyCode(invoice.currency)}</td>
                          <td>{formatMoney(invoice.tax_cents ?? 0)} {formatCurrencyCode(invoice.currency)}</td>
                          <td>{formatMoney(invoice.total_cents ?? invoice.amount_due_cents)} {formatCurrencyCode(invoice.currency)}</td>
                          <td className="text-capitalize">{String(invoice.status || '—').replace(/_/g, ' ')}</td>
                          <td>{formatMoney(invoice.amount_paid_cents)} {formatCurrencyCode(invoice.currency)}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              {invoice.hosted_invoice_url && (
                                <a href={invoice.hosted_invoice_url} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
                                  View
                                </a>
                              )}
                              {invoice.invoice_pdf && (
                                <a href={invoice.invoice_pdf} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                                  PDF
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-muted small">No invoices have been recorded yet.</div>
              )}
            </div>

            <div className="mt-4 pt-3 border-top">
              <div className="fw-semibold mb-2">API Permissions</div>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="text-muted small">Monthly Limit</div>
                  <div className="fw-semibold">{formatRateLimitValue(summary.api_rate_limit)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Requests This Month</div>
                  <div className="fw-semibold">{formatNumber(summary.api_requests_current)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Remaining</div>
                  <div className="fw-semibold">
                    {summary.api_rate_limit == null
                      ? 'Unlimited'
                      : summary.api_requests_current != null
                      ? formatNumber(Math.max(Number(summary.api_rate_limit) - Number(summary.api_requests_current), 0))
                      : '—'}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Data Sources</div>
                  <div className="fw-semibold">{summary.api_src_limit || 'All Data Sources'}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Derived Data</div>
                  <div className="fw-semibold">{summary.api_src_derived_flag ? 'Yes' : 'No'}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">History Access</div>
                  <div className="fw-semibold">{formatHistoryAccess(summary.api_hist_limit)}</div>
                </div>
              </div>
              {summary.subscription?.scheduled_change && (
                <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                  <div className="fw-semibold mb-2">Scheduled Plan Entitlements Update</div>
                  <EntitlementChangesTable changes={summary.subscription.scheduled_change.entitlement_changes} />
                  <div className="text-muted mt-2">
                    Estimated changes take effect on {formatDateValue(summary.subscription.scheduled_change.effective_on)}.
                    Final permissions reflect the plan terms in effect on that date.
                  </div>
                </div>
              )}
              {summary.subscription?.grandfather_sunset && (
                <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                  <div className="fw-semibold mb-2">Legacy Plan Entitlements Update</div>
                  <EntitlementChangesTable changes={summary.subscription.grandfather_sunset.entitlement_changes} />
                  <div className="text-muted mt-2">
                    Changes take effect on {formatDateValue(summary.subscription.grandfather_sunset.effective_change_date || summary.subscription.grandfather_sunset.grandfather_end_date)}.
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 pt-3 border-top">
              <div className="fw-semibold mb-2">Account Features</div>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="text-muted small">SSO / Custom IdP</div>
                  <div className="fw-semibold">{summary.subscription?.account_features?.sso_custom_idp ? 'Included' : 'Not Included'}</div>
                </div>
              </div>
              <AccountFeatureChanges
                changes={summary.subscription?.scheduled_change?.account_feature_changes}
                effectiveLabel={summary.subscription?.scheduled_change
                  ? `Estimated changes take effect on ${formatDateValue(summary.subscription.scheduled_change.effective_on)}.`
                  : ''}
              />
              <AccountFeatureChanges
                changes={summary.subscription?.grandfather_sunset?.account_feature_changes}
                effectiveLabel={summary.subscription?.grandfather_sunset
                  ? `Changes take effect on ${formatDateValue(summary.subscription.grandfather_sunset.effective_change_date || summary.subscription.grandfather_sunset.grandfather_end_date)}.`
                  : ''}
              />
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default OpsOrgSummary;
